import { REGEX_EMAIL } from "../../../utils/regex.js";

/**
 * Login-as menu order: most important role first.
 * Strings match the role constants (superAdmin, advertiser, marketer, designer, viewer).
 * Any other role sorts after these, then by name and email.
 */
export const LOGIN_AS_ROLE_ORDER_LIST = Object.freeze([
  "superAdmin",
  "admin",
  "advertiser",
  "marketer",
  "designer",
  "viewer",
]);

export const getRolePriority = (role, roleOrderList) => {
  if (!role || typeof role !== "string" || !Array.isArray(roleOrderList)) {
    return 1000;
  }
  const index = roleOrderList.indexOf(role);
  return index >= 0 ? index : 1000;
};

/**
 * @param {Array<{ role?: string, name?: string, email?: string }>} users
 * @param {string[]} roleOrderList
 * @returns {Array<typeof users[number]>}
 */
export const sortUsersByRolePriority = (users, roleOrderList) => {
  if (!Array.isArray(users)) return [];
  return [...users].sort((userA, userB) => {
    const priorityA = getRolePriority(userA?.role, roleOrderList);
    const priorityB = getRolePriority(userB?.role, roleOrderList);
    if (priorityA !== priorityB) return priorityA - priorityB;
    const nameA = `${userA?.name || ""}${userA?.email || ""}`;
    const nameB = `${userB?.name || ""}${userB?.email || ""}`;
    return nameA.localeCompare(nameB);
  });
};

/**
 * Active users with a valid email, most important role first.
 * @param {Array<{ email?: string, isDeleted?: boolean, role?: string, name?: string }>} users
 * @param {string[]} [roleOrderList]
 */
export const getLoginAsCandidateUsers = (
  users,
  roleOrderList = LOGIN_AS_ROLE_ORDER_LIST
) => {
  if (!Array.isArray(users)) return [];

  const candidates = [];
  for (const user of users) {
    if (!user || user.isDeleted === true) continue;
    // Matches OWNER_ROLE. The login-as endpoint always rejects that role.
    // constants.js is not importable under node --test.
    if (user.role === "owner") continue;
    if (typeof user.email !== "string") continue;
    const email = user.email.trim();
    if (!email || !REGEX_EMAIL.test(email)) continue;
    candidates.push(email === user.email ? user : { ...user, email });
  }

  return sortUsersByRolePriority(candidates, roleOrderList);
};

/**
 * Users for the agency login-as menu.
 * Concatenates every advertiser, drops deleted and owner users, sorts by the
 * login-as role order, then keeps the first user for each email.
 * @param {{ advertisers?: Array<{ advertiser?: { users?: unknown[] } }> }} agencyGroup
 */
export const getLoginAsAgencyUsers = (agencyGroup) => {
  const advertisers = agencyGroup?.advertisers;
  if (!Array.isArray(advertisers)) return [];

  const users = [];
  for (const item of advertisers) {
    const advertiserUsers = item?.advertiser?.users;
    if (!Array.isArray(advertiserUsers)) continue;
    users.push(...advertiserUsers);
  }

  const seenEmails = new Set();
  const deduped = [];
  for (const user of getLoginAsCandidateUsers(users)) {
    const key = user.email.trim().toLowerCase();
    if (seenEmails.has(key)) continue;
    seenEmails.add(key);
    deduped.push(user);
  }
  return deduped;
};
